import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { istUpdateVerfuegbar, type DeployVersion } from '../models/deploy-version.models';

/**
 * Deploy-Konsistenz-Check (F7): Laeuft im Browser ein aelterer Frontend-Stand
 * als der deployte?
 *
 * WARUM (Vorfall 2026-08-09): Angular laedt gehashte Chunks. Nach einem
 * Frontend-Deploy sitzen offene Sitzungen auf einer alten `index.html`, die auf
 * Chunks verweist, die es nicht mehr gibt. Das scheitert STILL — der Nutzer
 * sieht keine Fehlermeldung, nur eine Funktion, die nicht reagiert. Genau das
 * ist passiert: Ein Login blieb haengen, ohne Fehler, und erst ein harter
 * Reload half. Serverseitig war zu diesem Zeitpunkt alles in Ordnung (im
 * Access-Log ausschliesslich 200er) — der Zustand war im Browser.
 *
 * WIE: Das Frontend kennt seine eigene Kennung aus `environment.buildId` (vom
 * Image-Build eingesetzt), das Backend meldet die seine unter
 * `GET /api/v1/version`. Die Pipeline setzt beide aus demselben
 * `$CI_COMMIT_SHA`, sie laufen also im Gleichschritt. Ein Unterschied bedeutet
 * zuverlaessig: im Browser liegt ein alter Stand.
 *
 * BEWUSST KONSERVATIV: Im Zweifel KEIN Hinweis. Ist eine der beiden Kennungen
 * unbekannt (lokal, Platzhalter nicht ersetzt, ENV nicht gesetzt), wird nicht
 * verglichen. Ein Banner, das dauerhaft faelschlich anzeigt, wuerde ignoriert
 * werden und den Mechanismus wertlos machen.
 */
@Injectable({ providedIn: 'root' })
export class DeployVersionService {
  private readonly http = inject(HttpClient);

  /** Zuletzt vom Backend gemeldete Build-Kennung; `null` = noch nicht bekannt. */
  private readonly _deployed = signal<string | null>(null);
  readonly deployed = this._deployed.asReadonly();

  /** Eigene Kennung, beim Image-Build in environment.prod.ts eingesetzt. */
  readonly eigene: string = environment.buildId;

  /**
   * `true`, sobald das Backend eine ANDERE, bekannte Kennung meldet.
   * Die Entscheidungslogik liegt in `istUpdateVerfuegbar()` — als reine
   * Funktion vollstaendig testbar, unabhaengig von `environment.buildId`
   * (das im Test immer `'dev'` ist).
   */
  readonly updateVerfuegbar = computed<boolean>(() => istUpdateVerfuegbar(this.eigene, this._deployed()));

  /**
   * Holt die Build-Kennung des Backends und aktualisiert das Signal.
   * Lazy — der Aufrufer muss abonnieren.
   *
   * Fehler werden NICHT geschluckt, sondern an den Aufrufer gegeben: Das
   * Banner ignoriert sie bewusst (ein nicht erreichbarer Endpoint darf keinen
   * Hinweis erzeugen), aber ein Test soll den Fehlerpfad sehen koennen.
   */
  refresh(): Observable<DeployVersion> {
    // RELATIVE URL ohne `environment.apiUrl`: Der api.interceptor setzt das
    // Praefix selbst (`api.interceptor.ts`: `req.url.startsWith('http') ?
    // req.url : \`${environment.apiUrl}${req.url}\``). Mit explizitem Praefix
    // entstuende `/api/v1/api/v1/version`. Der MaintenanceStatusService
    // schreibt das Praefix aus, weil er bewusst `HttpBackend` nutzt und den
    // Interceptor umgeht — dieser Service hat diesen Grund nicht und laeuft
    // normal durch die Kette (inkl. Mandant-Header und 401-Refresh).
    return this.http
      .get<DeployVersion>('/version', { withCredentials: true })
      .pipe(tap((v) => this._deployed.set(v.version)));
  }
}
